import { FeatureFlag } from '@ps/web-core-enums';
import { select, useDispatch, useFeature } from '@ps/web-core-store';
import { generatePath } from '@ps/web-core-utils';
import { useTrackers } from '@ps/web-shared-analytics';
import * as Sentry from '@sentry/react';
import type React from 'react';
import { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import useSignup from '../hooks/useSignup';

const getCashGamesPathBasedOnSpade = (navigationPathName: string, cashGamesAliases: string) => {
  switch (navigationPathName) {
    case 'omaha':
    case 'holdem':
      return generatePath(cashGamesAliases, { view: 'simple', tab: navigationPathName });
    case 'all-in':
      return generatePath(cashGamesAliases, { view: 'all-in' });
    default:
      return generatePath(cashGamesAliases, { view: 'simple', tab: 'holdem' });
  }
};

const SpadeEmbeddedAPIProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const history = useHistory();
  const { modal } = useDispatch();
  const { buttonPressed } = useTrackers();
  const { idpToken, installId } = useSelector(select.session.get);
  const { pokerCashGames } = useSelector(select.cms.getAliases);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const spadeAnalyticsEnabled = useFeature(FeatureFlag.SpadeEnableAnalytics);
  const signup = useSignup();

  useEffect(() => {
    const subscriptions: ({ off: () => void } | undefined)[] = [];

    try {
      const loginSubscription = window.Spade?.api?.on('spade.auth.login', () => {
        if (!isAuthenticated) {
          if (spadeAnalyticsEnabled) {
            buttonPressed({ buttonName: 'Login To Play' });
          }

          modal.enqueue({ path: '/auth/login' });
        }
      });
      const signUpSubscription = window.Spade?.api?.on('spade.auth.signUp', () => {
        if (!isAuthenticated) {
          if (spadeAnalyticsEnabled) {
            buttonPressed({ buttonName: 'Sign Up' });
          }

          signup();
        }
      });
      const navigationSubscription = window.Spade?.api?.on('spade.app.navigated', (data) => {
        if (pokerCashGames) {
          const path = getCashGamesPathBasedOnSpade(data.path, pokerCashGames);
          history.replace(path);
        }
      });
      subscriptions.push(loginSubscription, signUpSubscription, navigationSubscription);
    } catch (error) {
      Sentry.captureException(error);
    }
    return () => {
      subscriptions.forEach((subscription) => subscription?.off());
    };
  }, [
    modal,
    isAuthenticated,
    pokerCashGames,
    history,
    spadeAnalyticsEnabled,
    buttonPressed,
    signup
  ]);

  useEffect(() => {
    if (idpToken) {
      try {
        window.Spade?.api?.setToken({ token: idpToken });
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }, [idpToken]);

  useEffect(() => {
    if (installId) {
      try {
        window.Spade?.api?.setAppConfig({ installId, hostAppVersion: window.version });
      } catch (error) {
        Sentry.captureException(error);
      }
    }
  }, [installId]);

  return children;
};

export default SpadeEmbeddedAPIProvider;
